import React, { useState, useCallback } from 'react';
import { Box, Chip, Typography, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import FlagIcon from '@mui/icons-material/Flag';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import CountryDialog from './CountryDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface Country {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const CountriesListing: React.FC = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // ---------- Delete Country ----------
  const handleDeleteClick = (id: GridRowId) => {
    setDeleteId(String(id));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      const response = await DataService.delete(`api/Country/${deleteId}`, {});
      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: 'Country deleted successfully!',
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to delete country');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to delete country. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
  };

  // ---------- Edit Country ----------
  const handleEditClick = (country: Country) => {
    setSelectedCountry(country);
    setEditDialogOpen(true);
  };

  // ---------- Add Country ----------
  const handleAddCountry = () => {
    setSelectedCountry(null);
    setEditDialogOpen(true);
  };

  // ---------- Toggle Active/Inactive ----------
  const handleToggleActive = async (country: Country) => {
    setToggleLoading(country.id);

    try {
      // Use specific activate/deactivate endpoints with PATCH method
      const endpoint = country.isActive
        ? `api/Country/deactivate?id=${country.id}`
        : `api/Country/activate?id=${country.id}`;

      const response = await DataService.patch(endpoint, {});

      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: `Country ${country.isActive ? 'deactivated' : 'activated'} successfully!`,
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to update country status');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to update country status. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setToggleLoading(null);
    }
  };

  // ---------- Handle Dialog Success ----------
  const handleDialogSuccess = () => {
    setEditDialogOpen(false);
    setRefreshKey((prev) => prev + 1);
    dispatch(
      showAlert({
        message: selectedCountry ? 'Country updated successfully!' : 'Country created successfully!',
        type: 'success'
      })
    );
  };

  // ---------- Data Grid Columns ----------
  const columns: PaginatedDataGridColumn[] = [
    {
      field: 'name',
      headerName: 'Country Name',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagIcon color="primary" fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'code',
      headerName: 'ISO Code',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontWeight: 'medium'
          }}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={params.row.isActive ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => handleToggleActive(params.row)}
              disabled={toggleLoading === params.row.id}
              color={params.row.isActive ? 'success' : 'default'}
            >
              {params.row.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // ---------- Data Grid Actions (Toolbar) ----------
  const actions: PaginatedDataGridAction[] = [
    {
      key: 'add',
      title: 'Add Country',
      icon: <AddIcon />,
      onClick: handleAddCountry,
      disabled: () => false,
      color: 'primary'
    }
  ];

  // ---------- Fetch Countries Function ----------
  const fetchCountries = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
    }) => {
      console.log('fetchCountries called with params:', params);
      try {
        const response = await DataService.get('api/Country/all');

        if (response.ok) {
          const responseData = await response.json();
          console.log('API Response:', responseData); // Debug log

          // Handle the API response structure: { data: [...], success: true, message: null }
          let rawCountries: any[] = [];

          if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
            rawCountries = responseData.data;
          } else if (Array.isArray(responseData)) {
            rawCountries = responseData;
          } else {
            console.error('Unexpected response structure:', responseData);
            throw new Error('Invalid response structure');
          }

          // Map the API response to our Country interface (handle active vs isActive and isoCode vs code)
          const countries: Country[] = rawCountries.map(country => ({
            id: country.id,
            name: country.name,
            code: country.isoCode || country.code, // API uses 'isoCode' field
            isActive: country.active !== undefined ? country.active : country.isActive,
            createdAt: country.createdAt || new Date().toISOString(),
            updatedAt: country.updatedAt || new Date().toISOString()
          }));

          console.log('Raw countries from API:', rawCountries);
          console.log('Mapped countries:', countries);

          // Filter by search term if provided
          let filteredCountries = countries;
          if (params.search) {
            const searchLower = params.search.toLowerCase();
            filteredCountries = countries.filter(
              country =>
                country.name.toLowerCase().includes(searchLower) ||
                country.code.toLowerCase().includes(searchLower)
            );
          }

          // Apply sorting if provided
          if (params.sortModel && params.sortModel.length > 0) {
            const sortModel = params.sortModel[0];
            filteredCountries.sort((a, b) => {
              let aValue = a[sortModel.field as keyof Country];
              let bValue = b[sortModel.field as keyof Country];

              // Handle string comparison
              if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue as string).toLowerCase();
              }

              if (sortModel.sort === 'desc') {
                return aValue < bValue ? 1 : -1;
              }
              return aValue > bValue ? 1 : -1;
            });
          }

          // Apply pagination (DataGrid uses 0-based page indexing)
          const startIndex = params.page * params.pageSize;
          const paginatedCountries = filteredCountries.slice(startIndex, startIndex + params.pageSize);

          const totalPages = Math.ceil(filteredCountries.length / params.pageSize);

          const result = {
            items: paginatedCountries,  // Use 'items' instead of 'data'
            totalCount: filteredCountries.length,
            pageNumber: params.page + 1,  // DataGrid uses 0-based pages, but expects 1-based in response
            pageSize: params.pageSize,
            totalPages: totalPages,
            hasNext: params.page + 1 < totalPages,
            hasPrevious: params.page > 0
          };

          console.log('Final result being returned:', result);
          return result;
        } else {
          throw new Error(`Failed to fetch countries: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load countries. Please try again.';
        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
        return {
          items: [],
          totalCount: 0,
          pageNumber: params.page + 1,
          pageSize: params.pageSize,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false
        };
      }
    },
    [dispatch]
  );

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 'bold' }}>
        Countries Management
      </Typography>

      <PaginatedDataGrid
        key={refreshKey}
        title="Countries"
        columns={columns}
        useBackendPagination
        onDataFetch={fetchCountries}
        actions={actions}
        emptyMessage="No countries found"
      />

      {/* Delete Confirmation Dialog */}
      <SimpleConfirmationDialog
        open={!!deleteId}
        title="Delete Country"
        message="Are you sure you want to delete this country? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />

      {/* Add/Edit Country Dialog */}
      <CountryDialog
        open={editDialogOpen}
        country={selectedCountry}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />
    </Box>
  );
};

export default CountriesListing;